import { ComponentProps, useCallback, useContext, useEffect } from 'react';
import { TokenAccessor } from '../../types/API';

import { LtiViewer } from '../lti-viewer';
import MaterialDialogHeader from '../../../../pages/material/components/MaterialDialogHeader';
import ModalContext from '../../../../contexts/ModalContext';
import Dialog from '@pelckmans/elementis/components/dialog';
import { ContentItem } from './utils/response-decoders';
import { LoadingText } from '../exercise-manager/components/exercise-loading';

export type Platform = 'Pelckmans Basisonderwijs' | 'Pelckmans Portail' | 'Pelckmans Portaal' | 'Pelckmans NT2';

type Props = {
  id: string;
  loginUrl: string;
  onClose: (contentItem?: ContentItem | undefined) => void;
  tokenAccessor: TokenAccessor;
  studioGroup: string;
  'aria-label': string;
  headerProps: Pick<ComponentProps<typeof MaterialDialogHeader>, 'title' | 'tags' | 'titleTooltip'>;
  ltiResponseDecoder?: (jwt: string) => ContentItem | undefined;
  loadingText?: LoadingText;
};

// This modal can be used for multiple Lti features: deep linking, lti resource link launch,...
export default function LtiModal({
  loginUrl,
  onClose,
  tokenAccessor,
  id = 'lti-modal',
  studioGroup,
  headerProps,
  'aria-label': ariaLabel,
  ltiResponseDecoder,
  loadingText,
}: Props): JSX.Element {
  const { closeModal } = useContext(ModalContext);

  const handleClose = useCallback(
    (resource?: ContentItem) => {
      resource ? onClose(resource) : onClose();
      closeModal(id);
    },
    [onClose, closeModal, id],
  );

  useEffect(() => {
    type MessageEventData = { messageType: string; parameters: Record<string, string> };
    const handlePostMessage = (e: MessageEvent<MessageEventData>): void => {
      if (e.data.messageType !== 'LTI') return;

      const resource = ltiResponseDecoder?.(e.data.parameters.jwt);
      handleClose(resource);
    };

    window.addEventListener('message', handlePostMessage);
    return (): void => window.removeEventListener('message', handlePostMessage);
  }, [handleClose, ltiResponseDecoder]);

  const size: ComponentProps<typeof Dialog>['size'] = 'full';

  return (
    <Dialog id={id} modal onClose={() => handleClose()} size={size} aria-label={ariaLabel}>
      <MaterialDialogHeader
        title={headerProps.title}
        tags={headerProps.tags}
        onClose={() => handleClose()}
        titleTooltip={headerProps.titleTooltip}
        showCloseLabelAsText={size === 'full'}
      />
      <Dialog.Body className="exercise-modal-section">
        <LtiViewer
          iframeTitle={headerProps.title}
          loginUrl={loginUrl}
          studioGroup={studioGroup}
          tokenAccessor={tokenAccessor}
          loadingText={loadingText}
        />
      </Dialog.Body>
    </Dialog>
  );
}
